import TopNav from "ui/dashboard/topnav";
import SiteList from "../cameraMap/site-list";
import { useContext, useEffect, useState, useRef, useMemo } from "react";
import { AuthContext } from "context/AuthContext";
import PresetList from "./components/presetList";
import { getSiteList } from "../cameraMap/service";
import clsx from "clsx";
import {
  SvgLogo,
  SvgPlay,
  SvgPause,
  Svg1X,
  Svg05X,
  Svg2X,
  Svg4X,
  SvgDateSelection,
  SvgPlayback,
  SvgClockTime,
  SvgDownload,
  SvgFullscreen,
  SvgAlertPrevPage,
  SvgAlertNextPage,
} from "ui/svgs";
import { showToast } from "ui/components/common";
import { Popover } from "react-tiny-popover";
import { Radio } from "antd";
import moment from "moment";
import { softTitle, formatContextDate, downloadFromSrc } from "lib/utils";
import type { presetGuardPicType } from "./components/itemSlide";
import PresetGuardPicSlide from "./components/presetGuardPicSlide";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import type { SliderSingleProps } from "antd";
import Calendar from "pages/dashboard/schedule/components/calendar";
import { getPresetEvent, getInspectionCameras } from "./service";
import "./page.scss";
import PlaybackModal from "ui/playback/playback-modal";
import usePlayBackRtcService from "pages/dashboard/playback/service";
import SiteSelectPopover from "ui/components/site-select-popover";
import { CameraInfo, WsResDataType } from "lib/types";
import CameraList from "./components/camera-list";
import { useSearchParams } from "react-router-dom";
import DefaultPage from "./components/default-page";
import GridPage from "./components/grid-page";

dayjs.extend(timezone);
const tab1Name = "Grid";
const tab2Name = "Default";
export default function VirtualGuardProgressPage() {
  const { sendGetPlaybackRecordsRequest } = usePlayBackRtcService();
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const query = searchParams.get("query") || "";
  const [mode, setMode] = useState(tab1Name);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [selectedSite, setSelectedSite] = useState([]);
  const [playbackModalOpen, setPlaybackModalOpen] = useState(false);
  const [progressData, setProgressData] = useState<presetGuardPicType[]>([]);
  const [inspectCameras, setInspectCameras] = useState<CameraInfo[]>([]);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [curPreset, setCurPreset] = useState(null);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [playbackStreamStart, setPlaybackStreamStart] = useState<number>(-1);
  const [stagedDate, setStagedDate] = useState<Date>(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  });
  const [datePickerValue, setDatePickerValue] = useState<Date>();
  const [progressDataLoading, setProgressDataLoading] = useState(true);
  const [startEnd, setStartEnd] = useState<number[]>([0, 24]);

  useEffect(() => {
    if (!curPreset || !selectedCamera) {
      return;
    }

    // deep copy the date
    const date = new Date(stagedDate.getTime());
    date.setHours(startEnd[0]);
    const startAt = date.getTime();
    date.setHours(startEnd[1]);
    const endAt = date.getTime();
    setProgressData([]);
    setProgressDataLoading(true);
    getPresetEvent(
      user.orgId,
      selectedCamera.id,
      curPreset?.id,
      startAt,
      endAt,
    ).then((res) => {
      setProgressDataLoading(false);
      if (res.status !== 200) {
        showToast(1, "Failed to get preset event data");
        return;
      }

      setProgressData(res.data.data);
    });
  }, [selectedCamera, curPreset, stagedDate, startEnd]);

  useEffect(() => {
    return () => {
      setPlaybackStreamStart(-1);
    };
  }, [playbackModalOpen]);

  useEffect(() => {
    if (!stagedDate || !selectedCamera || !playbackModalOpen) {
      return;
    }

    const streamId = selectedCamera?.capabilities?.streamNum > 1 ? 1 : 0;
    const getPlaybackRecords = async () => {
      try {
        const res = await sendGetPlaybackRecordsRequest(
          selectedCamera?.id,
          streamId,
          stagedDate?.getTime() / 1000,
          stagedDate?.getTime() / 1000 + 24 * 60 * 60 - 1,
          selectedCamera?.orgId,
        );
        const data = (res as WsResDataType)?.playbackGetRecordsReply?.records;
        if (data?.length === 0) {
          showToast(1, "There is no recording in this day.");
          return;
        }
        console.log("wsMsgData records ", data);
        setPlaybackStreamStart(data[0]?.startedAt);
      } catch (error) {
        showToast(
          1,
          "There was a problem searching the history records. Please try again. Error: " +
            error?.message,
        );
      }
    };
    getPlaybackRecords();
  }, [stagedDate, selectedCamera, playbackModalOpen]);

  const handleCancelDate = () => {
    setIsDatePopoverOpen(false); // Close popover
  };

  const handleOkDate = () => {
    setIsDatePopoverOpen(false); // Close popover
    setStagedDate(datePickerValue);
  };

  useEffect(() => {
    const siteId = selectedSite?.at(0) || "";
    setCameraLoading(true);
    getInspectionCameras(siteId, query).then((res) => {
      setCameraLoading(false);
      if (res.status !== 200) {
        showToast(1, "Failed to fetch cameras. Please try again later.");
        return;
      }
      setInspectCameras(
        res.data.data?.filter((item) => item?.capabilities?.inspection) || [],
      );
    });
  }, [selectedSite, query]);

  const handlePlayback = () => {
    setPlaybackModalOpen(true);
    // navigate(`/dashboard/playback/${activeAlert?.cameraId}`, {
    //   state: {
    //     alertSeekStart: activeAlert?.startTime,
    //     activeAlertId: activeAlert?.id,
    //     siteName: activeAlert?.site?.name,
    //   },
    // });
  };

  return (
    <>
      <TopNav title="Progress">
        <div className="flex-grow pl-3 flex gap-2 items-center">
          <Popover
            isOpen={isDatePopoverOpen}
            positions={["bottom", "left", "right", "top"]} // preferred positions by priority
            onClickOutside={() => setIsDatePopoverOpen(false)}
            content={
              <div className="border rounded-xl shadow-xl bg-white min-w-44 p-4 w-[320px]">
                <Calendar
                  onChange={(v) => {
                    const date = v.toDate();
                    console.log("select time", date.getTime());
                    date.setHours(0, 0, 0, 0);
                    setDatePickerValue(date);
                  }}
                  shifts={null}
                  defaultDate={dayjs()}
                  children={null}
                />
                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    className="px-4 py-2 rounded text-sm text-blue-700 hover:bg-gray-100"
                    onClick={handleCancelDate}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded text-sm text-blue-700 hover:bg-blue-200"
                    onClick={handleOkDate}
                  >
                    OK
                  </button>
                </div>
              </div>
            }
          >
            <button
              className="flex items-center rounded-xl hover:bg-zinc-200 border border-neutral-400 px-3 py-2 h-[33px] min-w-32 justify-center gap-1"
              onClick={() => setIsDatePopoverOpen(!isDatePopoverOpen)}
            >
              <SvgDateSelection
                width="20"
                className="fill-blue-500 flex-shrink-0"
              />
              <span className="text-sm font-semibold text-left text-nowrap">
                {formatContextDate(stagedDate, {
                  month: "short",
                  day: "2-digit",
                })}
              </span>
            </button>
          </Popover>
          <SiteSelectPopover
            onSitesChange={setSelectedSite}
            selectedSites={selectedSite}
            multiSelect={false}
          />
        </div>
        {selectedCamera && (
          <button
            className="flex mr-4 h-10 items-center rounded-xl bg-blue-500 px-4 text-sm font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 gap-2"
            onClick={handlePlayback}
          >
            <SvgPlayback width="18" className="fill-white" />
            <p className="overflow-hidden text-nowrap text-ellipsis break-words">
              View Playback
            </p>
          </button>
        )}
        <Radio.Group
          value={mode}
          buttonStyle="solid"
          onChange={(e) => {
            setMode(e?.target?.value);
          }}
        >
          <Radio.Button value={tab1Name}>{tab1Name}</Radio.Button>
          <Radio.Button value={tab2Name}>{tab2Name}</Radio.Button>
        </Radio.Group>
      </TopNav>
      <div className="flex h-full flex-col md:flex-row md:overflow-hidden bg-white rounded-3xl mr-6 mb-6">
        <div className="w-full flex-none md:w-96 overflow-y-auto border-r border-neutral-200 p-5">
          <CameraList
            cameras={inspectCameras}
            selectedCamera={selectedCamera}
            loading={cameraLoading}
            currentPreset={curPreset}
            onPresetClick={(preset) => {
              setCurPreset(preset);
            }}
            onSelectedCamera={(camera) => {
              setSelectedCamera(camera);
            }}
          />
        </div>
        {mode === tab2Name ? (
          <DefaultPage
            selectedCamera={selectedCamera}
            curPreset={curPreset}
            progressData={progressData}
            startEnd={startEnd}
            setStartEnd={(startEnd) => setStartEnd(startEnd)}
            progressDataLoading={progressDataLoading}
          />
        ) : (
          <GridPage
            selectedCamera={selectedCamera}
            curPreset={curPreset}
            progressData={progressData}
            startEnd={startEnd}
            setStartEnd={(startEnd) => setStartEnd(startEnd)}
            progressDataLoading={progressDataLoading}
          />
        )}
        {playbackStreamStart > 0 && (
          <PlaybackModal
            orgId={selectedCamera?.orgId}
            visible={playbackModalOpen}
            onClose={() => setPlaybackModalOpen(false)}
            cameraId={selectedCamera?.id}
            //alertTime={playbackStreamStart * 1000}
            startAt={playbackStreamStart * 1000}
            endAt={stagedDate.getTime() + 24 * 60 * 60 * 1000 - 1}
            //initialSeekAt={playbackStreamStart * 1000}
            timelineInterval={24}
          />
        )}
      </div>
    </>
  );
}
